// The one place date handling lives. Everything goes through date-fns; moment
// and dayjs are gone, and an ESLint rule keeps them from coming back.
//
// The named helpers exist so the format strings are declared once — date-fns
// tokens differ from moment's in ways that fail quietly (`DD` is day-of-year,
// `YYYY` is week-year), so they are not something to retype at each call site.

import addDays from 'date-fns/addDays';
import formatDuration from 'date-fns/formatDuration';
import intervalToDuration from 'date-fns/intervalToDuration';
import parseISO from 'date-fns/parseISO';
import addHours from 'date-fns/addHours';
import addSeconds from 'date-fns/addSeconds';
import format from 'date-fns/format';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import isAfter from 'date-fns/isAfter';
import isBefore from 'date-fns/isBefore';
import isValid from 'date-fns/isValid';
import startOfDay from 'date-fns/startOfDay';
import subDays from 'date-fns/subDays';

export const toDate = (value) =>
  value instanceof Date ? value : new Date(value);

const formatSafely = (value, pattern) => {
  if (value == null || value === '') return '';
  const date = toDate(value);
  return isValid(date) ? format(date, pattern) : '';
};

// `MM/dd/yyyy hh:mm:ss aaa`. The moment original ended in a `z` token, but
// moment-timezone was never installed so it always rendered empty — the format
// carried a trailing space and no zone. Dropped rather than reproduced.
export const formatTimestamp = (value) =>
  formatSafely(value, 'MM/dd/yyyy hh:mm:ss aaa');

export const formatTimestamp24h = (value) =>
  formatSafely(value, 'MM/dd/yyyy HH:mm:ss');

export const formatLongDateTime = (value) =>
  formatSafely(value, "MMMM d, yyyy 'at' h:mm a");

export const formatShortDate = (value) => formatSafely(value, 'MMM d, yyyy');

// Same rendering as formatShortDate, but for a date-only string. `new Date()`
// reads 'YYYY-MM-DD' as UTC midnight, which lands on the previous day for any
// zone behind UTC; parseISO reads it as local midnight, so the day survives.
export const formatIsoDay = (isoDate) => {
  if (isoDate == null || isoDate === '') return '';
  const date = parseISO(isoDate);
  return isValid(date) ? format(date, 'MMM d, yyyy') : '';
};

export const formatNumericDate = (value) => formatSafely(value, 'M/d/yyyy');

// Sortable stamp for generated file names, never shown as a date.
export const fileStamp = (value = new Date()) =>
  formatSafely(value, 'yyyyMMdd');

export const formatShortDateTime = (value) =>
  formatSafely(value, 'MMM d, yyyy · h:mm aaa');

export const formatDayAndTime = (value) =>
  formatSafely(value, 'MMM d · h:mm aaa');

export const formatWeekdayDateTime = (value) =>
  formatSafely(value, 'EEEE d MMMM, yyyy · h:mm aaa');

// date-fns words this differently from moment: "about 2 hours ago" rather than
// "2 hours ago", and "1 minute ago" rather than "a few seconds ago". Deliberate
// — the phrasing comes from the locale and matching moment's exactly would mean
// hand-rolling a distance formatter.
export const timeAgo = (value) => {
  if (value == null || value === '') return '';
  const date = toDate(value);
  return isValid(date) ? formatDistanceToNow(date, { addSuffix: true }) : '';
};

export const toIso = (value) => {
  const date = toDate(value);
  return isValid(date) ? date.toISOString() : '';
};

export const isoDaysAgo = (days) => subDays(new Date(), days).toISOString();

export const secondsFrom = (value, seconds) =>
  addSeconds(toDate(value), Number(seconds) || 0);

export const isAfterNow = (value) => isAfter(toDate(value), new Date());

// Midnight on the given day, offset by whole hours — how the executions filter
// turns a picked date plus an hour selector into a request boundary.
export const dayStartWithHours = (value, hours) =>
  addHours(startOfDay(toDate(value)), Number(hours) || 0).toISOString();

export const daysBefore = (value, days) => subDays(toDate(value), days);
export const daysAfter = (value, days) => addDays(toDate(value), days);

export { isAfter, isBefore, isValid };

// "3m 07s" style elapsed time, counting up from a start date.
export const elapsedSince = (value) => {
  const total = Math.max(
    0,
    Math.floor((Date.now() - toDate(value).getTime()) / 1000),
  );
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
};

// The browser's IANA zone, e.g. "America/Sao_Paulo". Callers that build links
// for external dashboards need this; moment's `z` token returned an empty
// string here because moment-timezone was never a dependency.
export const secondsToHumanReadable = (seconds) => {
  const value = Number(seconds);

  if (!Number.isFinite(value) || value <= 0) {
    return '0 seconds';
  }

  return formatDuration(intervalToDuration({ start: 0, end: value * 1000 }));
};

export const currentTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch (e) {
    return '';
  }
};
